import React, { useState, useMemo } from 'react';

// Lightweight month view with range selection inspired by react-daterange-picker (https://projects.wojtekmaj.pl/react-daterange-picker/)
const getFiscalYearRange = (baseDate, offsetYears = 0) => {
  // FY in this app follows India-style fiscal year: Apr 1 → Mar 31
  const now = new Date(baseDate);
  now.setHours(0, 0, 0, 0);

  const fyStartMonth = 3; // April (0-indexed)
  const fyStartYear = (now.getMonth() >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1) + offsetYears;
  const start = new Date(fyStartYear, fyStartMonth, 1, 0, 0, 0, 0);
  const end = new Date(fyStartYear + 1, 2, 31, 23, 59, 59, 999); // Mar 31
  return { start, end };
};

const DateRangePicker = ({ value, onChange }) => {
  const [anchorDate, setAnchorDate] = useState(() => {
    const d = value?.startDate ? new Date(value.startDate) : getFiscalYearRange(new Date(), 0).start;
    d.setHours(0,0,0,0);
    return d;
  });

  const [range, setRange] = useState(() => ({
    start: value?.startDate ? new Date(value.startDate) : getFiscalYearRange(new Date(), 0).start,
    end: value?.endDate ? new Date(value.endDate) : getFiscalYearRange(new Date(), 0).end
  }));

  const monthLabel = useMemo(() => anchorDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }), [anchorDate]);

  const startOfMonth = useMemo(() => {
    const d = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
    d.setHours(0,0,0,0);
    return d;
  }, [anchorDate]);

  const startWeekday = startOfMonth.getDay(); // 0..6
  const daysInMonth = new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 1, 0).getDate();

  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const isSameDay = (a, b) => a && b && a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
  const inRange = (d) => range.start && range.end && d >= range.start && d <= range.end;

  const selectDay = (d) => {
    if (!range.start || (range.start && range.end)) {
      setRange({ start: d, end: null });
    } else if (range.start && !range.end) {
      const start = range.start <= d ? range.start : d;
      const end = range.start <= d ? d : range.start;
      setRange({ start, end });
      onChange && onChange({ startDate: start.toISOString(), endDate: end.toISOString(), label: `${start.toLocaleDateString()} - ${end.toLocaleDateString()}` });
    }
  };

  // Presets
  const today = new Date(); today.setHours(0,0,0,0);

  const setPreset = (type) => {
    let start, end, label;
    const now = new Date(); now.setHours(0,0,0,0);
    switch (type) {
      case 'today':
        start = new Date(now);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        label = 'Today';
        break;
      case 'yesterday': {
        const y = new Date(now);
        y.setDate(now.getDate() - 1);
        y.setHours(0, 0, 0, 0);
        start = new Date(y);
        end = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 23, 59, 59, 999);
        label = 'Yesterday';
        break;
      }
      case 'last-7-days': {
        const s = new Date(now);
        s.setDate(now.getDate() - 6);
        s.setHours(0, 0, 0, 0);
        start = s;
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        label = 'Last 7 Days';
        break;
      }
      case 'last-30-days': {
        const s = new Date(now);
        s.setDate(now.getDate() - 29);
        s.setHours(0, 0, 0, 0);
        start = s;
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        label = 'Last 30 Days';
        break;
      }
      case 'this-week': {
        const day = now.getDay();
        const diffToMonday = (day === 0 ? -6 : 1 - day);
        const monday = new Date(now);
        monday.setDate(now.getDate() + diffToMonday);
        monday.setHours(0,0,0,0);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23,59,59,999);
        start = monday; end = sunday; label = 'This Week';
        break;
      }
      case 'last-week': {
        const day = now.getDay();
        const diffToMonday = (day === 0 ? -6 : 1 - day);
        const thisMonday = new Date(now);
        thisMonday.setDate(now.getDate() + diffToMonday);
        thisMonday.setHours(0, 0, 0, 0);

        const lastMonday = new Date(thisMonday);
        lastMonday.setDate(thisMonday.getDate() - 7);
        lastMonday.setHours(0, 0, 0, 0);

        const lastSunday = new Date(lastMonday);
        lastSunday.setDate(lastMonday.getDate() + 6);
        lastSunday.setHours(23, 59, 59, 999);

        start = lastMonday; end = lastSunday; label = 'Last Week';
        break;
      }
      case 'next-week': {
        const day = now.getDay();
        const diffToMonday = (day === 0 ? -6 : 1 - day);
        const nextMonday = new Date(now);
        nextMonday.setDate(now.getDate() + diffToMonday + 7);
        nextMonday.setHours(0,0,0,0);
        const nextSunday = new Date(nextMonday);
        nextSunday.setDate(nextMonday.getDate() + 6);
        nextSunday.setHours(23,59,59,999);
        start = nextMonday; end = nextSunday; label = 'Next Week';
        break;
      }
      case 'this-month': {
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        const last = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        start = first; end = last; label = 'This Month';
        break;
      }
      case 'last-month': {
        const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const last = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        start = first; end = last; label = 'Last Month';
        break;
      }
      case 'this-fy': {
        const r = getFiscalYearRange(now, 0);
        start = r.start; end = r.end; label = 'Current FY';
        break;
      }
      case 'last-fy': {
        const r = getFiscalYearRange(now, -1);
        start = r.start; end = r.end; label = 'Last FY';
        break;
      }
      default:
        return;
    }
    setRange({ start, end });
    setAnchorDate(new Date(start));
    onChange && onChange({ startDate: start.toISOString(), endDate: end.toISOString(), label });
  };

  const days = [];
  // leading blanks
  for (let i=0; i<startWeekday; i++) days.push(null);
  // month days
  for (let d=1; d<=daysInMonth; d++) {
    days.push(new Date(anchorDate.getFullYear(), anchorDate.getMonth(), d));
  }

  return (
    <div className="date-range-picker">
      <div className="drp-header" tabIndex={0} onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') setAnchorDate(new Date(anchorDate.getFullYear(), anchorDate.getMonth()-1, 1));
        if (e.key === 'ArrowRight') setAnchorDate(new Date(anchorDate.getFullYear(), anchorDate.getMonth()+1, 1));
      }}>
        <button className="drp-nav" aria-label="Prev month" onClick={() => setAnchorDate(new Date(anchorDate.getFullYear(), anchorDate.getMonth()-1, 1))}>◀</button>
        <div className="drp-month" aria-live="polite">{monthLabel}</div>
        <button className="drp-nav" aria-label="Next month" onClick={() => setAnchorDate(new Date(anchorDate.getFullYear(), anchorDate.getMonth()+1, 1))}>▶</button>
      </div>
      <div className="drp-presets">
        <button className="drp-preset" onClick={() => setPreset('today')}>Today</button>
        <button className="drp-preset" onClick={() => setPreset('yesterday')}>Yesterday</button>
        <button className="drp-preset" onClick={() => setPreset('last-7-days')}>Last 7 Days</button>
        <button className="drp-preset" onClick={() => setPreset('this-week')}>This Week</button>
        <button className="drp-preset" onClick={() => setPreset('last-week')}>Last Week</button>
        <button className="drp-preset" onClick={() => setPreset('next-week')}>Next Week</button>
        <button className="drp-preset" onClick={() => setPreset('this-month')}>This Month</button>
        <button className="drp-preset" onClick={() => setPreset('last-month')}>Last Month</button>
        <button className="drp-preset" onClick={() => setPreset('this-fy')}>Current FY</button>
        <button className="drp-preset" onClick={() => setPreset('last-fy')}>Last FY</button>
      </div>
      <div className="drp-grid">
        {[ 'Sun','Mon','Tue','Wed','Thu','Fri','Sat' ].map(w => (
          <div key={w} className="drp-weekday">{w}</div>
        ))}
        {days.map((d, idx) => d ? (
          <button key={idx}
            className={`drp-day ${isSameDay(d, range.start) ? 'start' : ''} ${isSameDay(d, range.end) ? 'end' : ''} ${inRange(d) ? 'in-range' : ''} ${inRange(d) && !isSameDay(d, range.start) && !isSameDay(d, range.end) ? 'in-between' : ''}`}
            onClick={() => selectDay(d)}
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectDay(d); } }}
          >{d.getDate()}</button>
        ) : (
          <div key={idx} className="drp-blank" />
        ))}
      </div>
      <div className="drp-footer">
        <button className="btn-secondary small" onClick={() => { setRange({ start: null, end: null }); onChange && onChange('all'); }}>Clear</button>
      </div>
    </div>
  );
};

export default DateRangePicker;

